import { ref, computed, type Ref } from 'vue'
import { sdk } from '@/graphql/client'

export interface InstanceRights {
    id: number
    instance: { id: number; name: string }
    primary: boolean
    bzucoAdmin: boolean
    allowManageUsers: boolean
    allowManageEvents: boolean
    allowManageTicketsets: boolean
    allowManageTickets: boolean
    allowManageDevices: boolean
    allowStats: boolean
}

export interface User {
    id: number
    username: string
    firstName: string
    lastName: string
    email: string
    isActive: boolean
    isSuperuser: boolean
    isStaff: boolean
    lastLogin: string | null
    dateJoined: string
    profile: { validUntil: string | null } | null
    instances: InstanceRights[]
}

export type UserActiveFilter = 'all' | 'true' | 'false'

export interface UserFilters {
    search: string
    active: UserActiveFilter
}

function normalize(s: string): string {
    return s.normalize('NFD').replace(/\p{Diacritic}/gu, '').toLowerCase()
}

export function useUsersData() {
    const users: Ref<User[]> = ref([])
    const loading = ref(false)
    const error = ref<string | null>(null)

    const filters: Ref<UserFilters> = ref({
        search: '',
        active: 'all'
    })

    async function refetch() {
        loading.value = true
        error.value = null
        try {
            const r = await sdk.AdminUsersList()
            users.value = (r.admin?.user?.list ?? []) as unknown as User[]
        } catch (e) {
            error.value = (e as Error).message
            users.value = []
        } finally {
            loading.value = false
        }
    }

    const filteredUsers = computed<User[]>(() => {
        const f = filters.value
        const q = f.search ? normalize(f.search) : ''

        const filtered = users.value.filter(u => {
            if (q) {
                const hay = [u.username, u.firstName, u.lastName, u.email]
                    .map(normalize)
                    .join(' ')
                if (!hay.includes(q)) return false
            }
            if (f.active === 'true' && !u.isActive) return false
            if (f.active === 'false' && u.isActive) return false
            return true
        })

        // Sort: lastName asc, then firstName asc, nulls last (cs locale)
        return filtered.slice().sort((a, b) => {
            const al = a.lastName || ''
            const bl = b.lastName || ''
            if (al === '' && bl !== '') return 1
            if (al !== '' && bl === '') return -1
            const lc = al.localeCompare(bl, 'cs')
            if (lc !== 0) return lc
            return (a.firstName || '').localeCompare(b.firstName || '', 'cs')
        })
    })

    return { users, filters, filteredUsers, loading, error, refetch }
}
